import * as React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 gap-2',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-font-color-1 hover:bg-secondary hover:text-primary text-[0.875rem] leading-[1.5rem] font-bold',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        destructiveOutline:
          'border border-destructive text-destructive hover:bg-destructive hover:text-white',
        success: 'bg-success text-white hover:bg-success/70 font-bold',
        successOutline:
          'border border-success text-success hover:bg-success hover:text-white',
        outline:
          'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary:
          'border border-secondary bg-transparent hover:bg-secondary hover:text-primary text-[0.875rem] leading-[1.5rem] font-bold',
        action:
          'border-grey-2 border bg-white hover:border-secondary hover:bg-secondary hover:text-primary text-[0.875rem] leading-[1.5rem] font-bold',
        text: 'text-secondary rounded-none border-b border-secondary text-[0.875rem] leading-[1.5rem] font-bold',
        textSuccess:
          'text-success rounded-none border-b border-success text-[0.875rem] leading-[1.5rem] font-bold',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-secondary underline underline-offset-4 hover:bg-background-4 text-[0.875rem] leading-[1.5rem]',
      },
      size: {
        default: 'px-8 py-4 min-w-48',
        sm: 'px-4 py-2 min-w-48',
        none: 'p-0 pb-2 rounded-none gap-1 hover:gap-4',
        lg: 'h-10 px-8',
        icon: 'h-16 w-16 p-4',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({className, variant, size, asChild = false, ...props}, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({variant, size, className}))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export {Button, buttonVariants};
